import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CONSENT_TEXT_VERSION, consentText, intervieweeStrings, type LiveTurn, type ServerMessage, type SessionSummary } from "@gk/shared";
import { LiveClient, type ConnStatus } from "../lib/live";

const NATIVE: Record<string, string> = {
  "en-US": "English",
  "en-IN": "English",
  "hi-IN": "हिन्दी",
  "mr-IN": "मराठी",
  "es-MX": "Español",
  "es-US": "Español",
  "pt-BR": "Português",
  "fr-FR": "Français",
};

/**
 * The participant's phone. Almost no words: the consent, then the
 * interviewer's words in large type, spoken aloud, and a soft light that
 * shows when the phone is listening.
 */
export function JoinPage() {
  const { code } = useParams();
  const client = useRef<LiveClient | null>(null);
  const [session, setSession] = useState<SessionSummary | null>(null);
  const [state, setState] = useState<string>("pairing");
  const [conn, setConn] = useState<ConnStatus>("connecting");
  const [lang, setLang] = useState<string>("es-MX");
  const [latest, setLatest] = useState<LiveTurn | null>(null);
  const [partial, setPartial] = useState<string | null>(null);
  const [own, setOwn] = useState<string>("");
  const [speaking, setSpeaking] = useState(false);
  const [audioOn, setAudioOn] = useState(false);
  const [problem, setProblem] = useState<"code" | "mic" | null>(null);
  const [declined, setDeclined] = useState(false);
  const lastFinalAt = useRef(0);

  useEffect(() => {
    const c = new LiveClient({
      role: "interviewee",
      interviewKey: `join:${code}`,
      hello: { role: "interviewee", token: code! },
      onStatus: setConn,
      onMessage: (m: ServerMessage) => {
        switch (m.t) {
          case "welcome": {
            setSession(m.session);
            setState(m.session.state);
            setLang(m.session.intervieweeLang);
            const last = [...m.turns].reverse().find((x) => x.speaker === "interviewer");
            if (last) setLatest(last);
            break;
          }
          case "state":
            setState(m.state);
            if (m.intervieweeLang) setLang(m.intervieweeLang);
            break;
          case "turn":
            if (m.turn.speaker === "interviewer") {
              setLatest(m.turn);
              setPartial(null);
              lastFinalAt.current = Date.now();
            } else {
              setOwn(m.turn.sourceText);
            }
            break;
          case "translation":
            // A partial that arrives just after its final is stale.
            if (!m.final && Date.now() - lastFinalAt.current > 1500) setPartial(m.text);
            break;
          case "caption":
            if (m.speaker === "interviewee") setOwn(m.text);
            break;
          case "voice-start":
            setSpeaking(true);
            break;
          case "voice-end":
            setTimeout(() => setSpeaking(false), 400);
            break;
          case "error":
            if (m.code === "pairing") setProblem("code");
            break;
        }
      },
    });
    client.current = c;
    return () => c.close();
  }, [code]);

  const base = lang.split("-")[0]!;
  const s = intervieweeStrings(base);
  const text = useMemo(
    () => (session ? consentText(base, session.country, session.controller) : ""),
    [base, session],
  );

  const enableAudio = async () => {
    try {
      await client.current!.enableAudio();
      setAudioOn(true);
      setProblem(null);
      return true;
    } catch {
      setProblem("mic");
      return false;
    }
  };

  const agree = async () => {
    if (!(await enableAudio())) return;
    client.current!.send({ t: "consent", accepted: true, lang, textVersion: CONSENT_TEXT_VERSION });
  };

  const live = state === "live";
  return (
    <div className="ie-app" lang={lang}>
      <div className="brand-line">
        <img src="/icon.svg" width={22} height={22} alt="" />
        <span>{session?.controller ?? ""}</span>
        <span className="spacer" />
        {conn === "reconnecting" && <span>{s.reconnecting}</span>}
      </div>

      <div className="ie-stage">
        {problem === "code" && <p className="ie-said" style={{ fontSize: 26 }}>{s.codeExpired}</p>}

        {!problem && !session && <p className="ie-own">…</p>}

        {session && (state === "pairing" || state === "consent") && !declined && (
          <div className="ie-consent">
            {session.intervieweeLanguages.length > 1 && (
              <div className="ie-langs" role="group" aria-label={s.chooseLanguage}>
                {session.intervieweeLanguages.map((l) => (
                  <button
                    key={l}
                    className={l === lang ? "on" : ""}
                    onClick={() => {
                      setLang(l);
                      client.current?.send({ t: "lang", lang: l });
                    }}
                  >
                    {NATIVE[l] ?? l}
                  </button>
                ))}
              </div>
            )}
            <h1 style={{ fontSize: 30 }}>{s.consentTitle}</h1>
            <div className="text">{text}</div>
            {problem === "mic" && <p className="error-box">{s.micBlocked}</p>}
            <div className="ie-actions">
              <button className="btn primary" onClick={() => void agree()}>
                {s.consentAccept}
              </button>
              <button
                className="btn"
                onClick={() => {
                  setDeclined(true);
                  client.current?.send({ t: "consent", accepted: false, lang, textVersion: CONSENT_TEXT_VERSION });
                }}
              >
                {s.consentDecline}
              </button>
            </div>
          </div>
        )}

        {declined && <p className="ie-said">{s.consentDeclined}</p>}

        {(state === "ready" || state === "paused") && !declined && (
          <>
            <p className="ie-said partial">{state === "paused" ? s.paused : s.waiting}</p>
            <p className="ie-own">{s.yourTurnHint}</p>
          </>
        )}

        {live && (
          <>
            <p className={`ie-said ${partial ? "partial" : ""}`} aria-live="polite">
              {partial ?? latest?.targetText ?? s.waiting}
            </p>
            <p className="ie-own" aria-live="polite">{own}</p>
            {!audioOn && (
              <button className="btn primary big" onClick={() => void enableAudio()}>
                {s.tapToEnableSound}
              </button>
            )}
          </>
        )}

        {state === "ended" && !declined && (
          <>
            <p className="ie-said">{s.thanks}</p>
            <p className="ie-own">{s.ended}</p>
          </>
        )}
      </div>

      {live && (
        <div className={`ie-glow ${speaking ? "speaking" : "on"}`} aria-label={s.listening} role="img">
          <i />
        </div>
      )}
    </div>
  );
}
